export type BookingStatus =
  | 'confirmed'
  | 'pending'
  | 'approved'
  | 'declined'
  | 'cancelled';

/** Snapshot of an AddOn at booking time. Frozen so the host editing the
 * listing later doesn't retroactively reprice existing bookings. */
export interface IBookingAddOn {
  id: string;
  label: string;
  unit: 'per stay' | 'per night' | 'per person' | 'per unit';
  unitPrice: number;
  /** Quantity for `per unit` add-ons; 1 for all others. */
  quantity: number;
  /** Total billed for this line (unitPrice × multiplier at booking time). */
  amount: number;
  /** Material Symbols name captured at booking time; receipts render this even
   * if the host later changes the source add-on's icon. */
  icon?: string;
  /** Optional photo data URL captured at booking time; same rationale as icon. */
  photo?: string;
  /** Real backend `IListingExtra.id` (see IAddOn.realExtraId) — undefined for
   *  mock bookings or add-ons that were never synced to the real backend. */
  realExtraId?: number;
}

export interface IBooking {
  id: string;
  userEmail: string;
  listingId: number;
  listingTitle: string;
  listingLocation: string;
  listingPhoto: string;
  hostName: string;
  hostAddress?: string;
  lat?: number;
  lng?: number;
  dates: { start: string; end: string };
  nights: number;
  guests: number;
  rvSummary: string;
  pricePerNight: number;
  subtotal: number;
  cleaningFee: number;
  serviceFee: number;
  /** Optional $5/night charge that refunds as CurbNTurf Cash after a qualifying
   * review. Undefined on bookings made before the feedback-incentive model. */
  feedbackIncentive?: number;
  total: number;
  instantBook: boolean;
  status: BookingStatus;
  createdAt: string;
  contact: { email: string; phone?: string };
  /** Optional opening note from the guest — surfaces in the inbox thread. */
  requestMessage?: string;
  /** Optional reason supplied at cancel time — surfaces in the inbox thread. */
  cancelReason?: string;
  /** ISO when the booking was last modified (dates/guests/add-ons). */
  modifiedAt?: string;
  /** Snapshot of add-ons attached to this booking. */
  addOns?: IBookingAddOn[];
  /** Sum of all add-on amounts at booking/modification time. */
  addOnsTotal?: number;
  /** ISO when the guest left a review for this completed trip. */
  reviewedAt?: string;
  /** ISO when the host left a review for this guest. Parallel to reviewedAt;
   * both are backend-derived (`reservation.guestRated`/`listingRated`) and
   * drive the two-sided reveal — the backend shows either review to the
   * world once both are set, or after its own 21-day auto-reveal cutoff. */
  hostReviewedAt?: string;
  /** Reward credit dollars applied to this booking's total (subtracts from availableCredit). */
  creditApplied?: number;
  /** Real backend `IReservation.id` — present when this booking is backed by a
   *  real reservation (vs. a purely local mock booking). Mutating actions
   *  (cancel/modify/hostDecide/hostCancel) route to the real `/reservation`
   *  endpoints when this is set. */
  realReservationId?: number;
  /** Real backend `ISite.id` the reservation was made against. */
  realSiteId?: number;
  /** Real backend `IReservation.user.id` — the guest's numeric user id.
   *  Needed to submit a host→guest rating (`POST rating/guest/:guestId`). */
  guestUserId?: number;
  /** Real backend listing owner's user id — tells a host viewing the booking
   *  apart from the guest. */
  hostUserId?: number;
  /** Real backend `IReservation.rvId` — kept on modify so a date change doesn't
   *  swap the rig for whatever RV is currently active. */
  realRvId?: number;
  /** Discount lines already taken off `total` (e.g. First Reservation Discount). */
  discounts?: { label: string; amount: number }[];
  /** Taxes included in `total`. */
  taxTotal?: number;
  /** Listing cancellation policy (`easy` | `moderate` | `strict` | `exclusive`). */
  cancellationPolicy?: string;
  /** Guest asked to change this booking and the host hasn't answered yet
   *  (real status `change`). `status` is `pending` so host approval queues pick
   *  it up, but unlike a new request the original booking is already paid and
   *  stands until the host approves the change. */
  changeRequested?: boolean;
  /** The requested dates/total while `changeRequested`. */
  pendingChange?: { start: string; end: string; nights: number; total: number };
}

export type ReviewStatus = 'not-reviewed' | 'awaiting-other-party' | 'published';

/** Derives the two-sided review reveal state from a booking's own
 *  `reviewedAt`/`hostReviewedAt` timestamps — these are already backend-derived
 *  (`reservation.listingRated`/`guestRated`), so no separate reservation status
 *  field is needed. Pass `('reviewedAt', 'hostReviewedAt')` for a guest-facing
 *  view, or the swapped pair for a host-facing view of the same booking. */
export function reviewStatus(
  b: Pick<IBooking, 'reviewedAt' | 'hostReviewedAt'>,
  mine: 'reviewedAt' | 'hostReviewedAt',
  theirs: 'reviewedAt' | 'hostReviewedAt',
): ReviewStatus {
  if (!b[mine]) return 'not-reviewed';
  return b[theirs] ? 'published' : 'awaiting-other-party';
}

/** `bg`/`color` are a soft tint for light surfaces; `solid` is an opaque
 *  fill + text color for badges drawn over photos. */
export const STATUS_META: Record<BookingStatus, { label: string; color: string; bg: string; solid: string }> = {
  confirmed: { label: 'Confirmed', color: '#295d42', bg: 'bg-jungle-green/15', solid: 'bg-jungle-green text-white' },
  pending:   { label: 'Pending',   color: '#b3760e', bg: 'bg-gold/30',         solid: 'bg-gold text-dark-text' },
  approved:  { label: 'Approved',  color: '#295d42', bg: 'bg-jungle-green/15', solid: 'bg-jungle-green text-white' },
  declined:  { label: 'Declined',  color: '#9a3f0a', bg: 'bg-trinidad/15',     solid: 'bg-trinidad text-white' },
  cancelled: { label: 'Cancelled', color: '#555555', bg: 'bg-dark-text/10',    solid: 'bg-dark-text text-white' },
};
